import { Fragment, useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Divider,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

// Contexts and Components
import { usePermission, PERMISSION_TYPES, RESOURCE_TYPES } from '../../contexts/PermissionContext';
import { ROLES, useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeaderCard } from '../../components/common';

// Services
import { usersAPI, groupsAPI, projectsAPI, tasksAPI } from '../../services/api';

const MATRIX_COL_WIDTHS = {
  expand: 50,
  type: 160,
  effective: 150,
};

const PermissionSettings = () => {
  const {
    getEffectivePermission,
    setPermission,
    removePermission,
    getDirectPermission,
    registerResource,
    loadSubjectPermissions,
    saveSubjectPermissions,
    clearPermissions,
    SUBJECT_TYPES,
  } = usePermission();
  const { getActiveCompany, hasAnyRole, hasRole } = useAuth();
  const { showError, showSuccess } = useNotification();

  const [selectedSubjectKey, setSelectedSubjectKey] = useState('');
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [projectTree, setProjectTree] = useState([]);
  const [isLoadingAssignees, setIsLoadingAssignees] = useState(false);
  const [isLoadingMatrix, setIsLoadingMatrix] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
  const activeCompanyId = activeCompany?.id || null;
  const canManagePermissions = hasAnyRole([ROLES.ADMIN, ROLES.COMPANY_ADMIN]);
  const canViewPermissions = canManagePermissions || hasRole(ROLES.MANAGER);

  const parseSubjectKey = useCallback((key) => {
    if (!key) return { subjectType: null, subjectId: null };
    const [subjectType, ...parts] = key.split(':');
    return { subjectType, subjectId: parts.join(':') };
  }, []);

  // Combine users and teams
  const allAssignees = useMemo(() => {
    const mappedUsers = users.map((u) => ({
      ...u,
      type: SUBJECT_TYPES.USER,
      key: `${SUBJECT_TYPES.USER}:${u.id}`,
    }));
    const mappedGroups = groups.map((g) => ({
      ...g,
      type: SUBJECT_TYPES.GROUP,
      key: `${SUBJECT_TYPES.GROUP}:${g.id}`,
    }));
    return [...mappedUsers, ...mappedGroups];
  }, [users, groups, SUBJECT_TYPES.USER, SUBJECT_TYPES.GROUP]);

  const selectedAssignee = useMemo(() => {
    return allAssignees.find((a) => a.key === selectedSubjectKey) || null;
  }, [selectedSubjectKey, allAssignees]);

  const loadAssignees = useCallback(async () => {
    if (!activeCompanyId) {
      setUsers([]);
      setGroups([]);
      return;
    }

    setIsLoadingAssignees(true);
    try {
      const [usersResponse, groupsResponse] = await Promise.all([
        usersAPI.listByCompany(activeCompanyId, {
          page: 1,
          pageSize: 200,
          sortBy: 'name',
          sortOrder: 'asc',
        }),
        groupsAPI.listByCompany(activeCompanyId),
      ]);

      const userItems = Array.isArray(usersResponse?.items) ? usersResponse.items : [];
      const normalizedUsers = userItems
        .map((user) => ({ id: user.id, name: user.name || user.email, role: user.role }));

      const normalizedGroups = Array.isArray(groupsResponse)
        ? groupsResponse.map((group) => ({ id: group.id, name: group.name }))
        : [];

      setUsers(normalizedUsers);
      setGroups(normalizedGroups);
    } catch (error) {
      setUsers([]);
      setGroups([]);
      showError(error.message || 'Failed to load users and groups');
    } finally {
      setIsLoadingAssignees(false);
    }
  }, [activeCompanyId, showError]);

  const loadProjectTree = useCallback(async () => {
    if (!activeCompanyId) {
      setProjectTree([]);
      return;
    }

    setIsLoadingMatrix(true);
    try {
      const projects = await projectsAPI.listByCompany(activeCompanyId, {
        page: 1,
        pageSize: 200,
        sortBy: 'name',
        sortOrder: 'asc',
      });

      const normalizedProjects = await Promise.all(
        (Array.isArray(projects) ? projects : []).map(async (project) => {
          const tasks = await tasksAPI.listByProject(project.id);
          const normalizedTasks = (Array.isArray(tasks) ? tasks : []).map((task) => ({
            id: task.id,
            name: task.name,
            subtasks: Array.isArray(task.subtasks)
              ? task.subtasks.map((subtask) => ({ id: subtask.id, name: subtask.name }))
              : [],
          }));

          normalizedTasks.forEach((task) => {
            registerResource(task.id, RESOURCE_TYPES.TASK, project.id);
            task.subtasks.forEach((subtask) => {
              registerResource(subtask.id, RESOURCE_TYPES.SUBTASK, task.id);
            });
          });

          return {
            id: project.id,
            name: project.name,
            tasks: normalizedTasks,
          };
        })
      );

      setProjectTree(normalizedProjects);
    } catch (error) {
      setProjectTree([]);
      showError(error.message || 'Failed to load company projects');
    } finally {
      setIsLoadingMatrix(false);
    }
  }, [activeCompanyId, registerResource, showError]);

  const loadSelectedSubjectPermissions = useCallback(async () => {
    const { subjectType, subjectId } = parseSubjectKey(selectedSubjectKey);
    if (!activeCompanyId || !subjectType || !subjectId) {
      clearPermissions();
      return;
    }

    setIsLoadingPermissions(true);
    try {
      await loadSubjectPermissions({
        companyId: activeCompanyId,
        subjectType,
        subjectId,
      });
    } catch (error) {
      showError(error.message || 'Failed to load permissions');
    } finally {
      setIsLoadingPermissions(false);
    }
  }, [activeCompanyId, selectedSubjectKey, parseSubjectKey, loadSubjectPermissions, clearPermissions, showError]);

  useEffect(() => {
    setSelectedSubjectKey('');
    clearPermissions();
    loadAssignees();
    loadProjectTree();
  }, [activeCompanyId, clearPermissions, loadAssignees, loadProjectTree]);

  useEffect(() => {
    loadSelectedSubjectPermissions();
  }, [loadSelectedSubjectPermissions]);

  const toggleProjectExpand = (projectId) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const handlePermissionChange = (resourceType, resourceId, newPermission) => {
    if (!selectedAssignee || !canManagePermissions) return;

    const subjectType = selectedAssignee.type;
    const subjectId = selectedAssignee.id;

    if (newPermission === null) {
      removePermission(subjectId, resourceType, resourceId, subjectType);
    } else {
      setPermission(subjectId, resourceType, resourceId, newPermission, subjectType);
    }
  };

  const handleSavePermissions = async () => {
    if (!canManagePermissions) {
      showError('You do not have permission to modify permissions');
      return;
    }

    const { subjectType, subjectId } = parseSubjectKey(selectedSubjectKey);
    if (!activeCompanyId || !subjectType || !subjectId) return;

    setIsSaving(true);
    try {
      await saveSubjectPermissions({
        companyId: activeCompanyId,
        subjectType,
        subjectId,
      });
      showSuccess('Permissions saved successfully');
    } catch (error) {
      showError(error.message || 'Failed to save permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const renderPermissionToggle = (resourceType, resourceId) => {
    if (!selectedAssignee) return null;

    const subjectType = selectedAssignee.type;
    const subjectId = selectedAssignee.id;

    const directPerm = getDirectPermission(subjectId, resourceType, resourceId, subjectType);
    const effectivePerm = getEffectivePermission(subjectId, resourceType, resourceId, subjectType);
    const isInherited = effectivePerm.inherited;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ToggleButtonGroup
          value={directPerm}
          exclusive
          onChange={(e, newValue) => handlePermissionChange(resourceType, resourceId, newValue)}
          size="small"
          disabled={!canManagePermissions}
        >
          <ToggleButton
            value={PERMISSION_TYPES.ALLOW}
            sx={{
              px: 1,
              '&.Mui-selected': {
                backgroundColor: 'success.light',
                color: 'success.dark',
                '&:hover': { backgroundColor: 'success.light' },
              },
            }}
          >
            <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />
            Allow
          </ToggleButton>
          <ToggleButton
            value={PERMISSION_TYPES.DENY}
            sx={{
              px: 1,
              '&.Mui-selected': {
                backgroundColor: 'error.light',
                color: 'error.dark',
                '&:hover': { backgroundColor: 'error.light' },
              },
            }}
          >
            <BlockIcon fontSize="small" sx={{ mr: 0.5 }} />
            Deny
          </ToggleButton>
        </ToggleButtonGroup>
        {directPerm && (
          <Tooltip title="Clear permission">
            <IconButton
              size="small"
              onClick={() => handlePermissionChange(resourceType, resourceId, null)}
              disabled={!canManagePermissions}
            >
              <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isInherited && effectivePerm.permission && (
          <Chip
            size={isMobile ? 'small' : 'medium'}
            label={effectivePerm.permission === PERMISSION_TYPES.ALLOW ? 'Inherited' : 'Blocked'}
            variant="outlined"
            color={effectivePerm.permission === PERMISSION_TYPES.ALLOW ? 'success' : 'error'}
          />
        )}
      </Box>
    );
  };

  const renderEffectiveStatus = (resourceType, resourceId) => {
    if (!selectedAssignee) return null;

    const subjectType = selectedAssignee.type;
    const subjectId = selectedAssignee.id;

    const effectivePerm = getEffectivePermission(subjectId, resourceType, resourceId, subjectType);

    if (!effectivePerm.permission) {
      return (
        <Chip
          icon={<RemoveCircleOutlineIcon />}
          label="No Access"
          size={isMobile ? 'small' : 'medium'}
          variant="outlined"
        />
      );
    }

    if (effectivePerm.permission === PERMISSION_TYPES.ALLOW) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label={effectivePerm.inherited ? 'Inherited' : 'Allowed'}
          size={isMobile ? 'small' : 'medium'}
          color="success"
          variant={effectivePerm.inherited ? 'outlined' : 'filled'}
        />
      );
    }

    return (
      <Chip
        icon={<CancelIcon />}
        label={effectivePerm.inherited ? 'Blocked' : 'Denied'}
        size={isMobile ? 'small' : 'medium'}
        color="error"
        variant={effectivePerm.inherited ? 'outlined' : 'filled'}
      />
    );
  };

  if (!canViewPermissions) {
    return <Alert severity="error">You do not have permission to access permission settings.</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <PageHeaderCard
        title="Permission Settings"
        subtitle="Manage user and team access to projects, tasks, and subtasks"
        icon={<SecurityIcon sx={{ color: 'warning.main', fontSize: { xs: 22, sm: 26 } }} />}
      />

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>DENY takes precedence over ALLOW.</strong> If a parent resource (Project/Task) is denied,
          all child resources are automatically blocked. Permissions cascade down the hierarchy.
        </Typography>
      </Alert>

      {!canManagePermissions && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have read-only access. Only company admins can modify permissions.
        </Alert>
      )}

      {/* Legend - Top */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Legend
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="Allowed" size={isMobile ? 'small' : 'medium'} color="success" />
            <Typography variant="caption">Direct ALLOW permission</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="Inherited" size={isMobile ? 'small' : 'medium'} color="success" variant="outlined" />
            <Typography variant="caption">Inherited from parent</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="Denied" size={isMobile ? 'small' : 'medium'} color="error" />
            <Typography variant="caption">Direct DENY permission</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="Blocked" size={isMobile ? 'small' : 'medium'} color="error" variant="outlined" />
            <Typography variant="caption">Blocked by parent DENY</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="No Access" size={isMobile ? 'small' : 'medium'} variant="outlined" />
            <Typography variant="caption">No permission set</Typography>
          </Box>
        </Box>
      </Paper>

      {/* User/Team Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {!activeCompanyId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Select an active company to manage permissions.
          </Alert>
        )}
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Select User or Team</InputLabel>
          <Select
            value={selectedSubjectKey}
            label="Select User or Team"
            onChange={(e) => setSelectedSubjectKey(e.target.value)}
            disabled={!activeCompanyId || isLoadingAssignees}
          >
            <MenuItem disabled sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>
              Users
            </MenuItem>
            {users.map((user) => (
              <MenuItem key={`user-${user.id}`} value={`${SUBJECT_TYPES.USER}:${user.id}`} sx={{ pl: 3 }}>
                {user.name} ({user.role})
              </MenuItem>
            ))}
            <Divider sx={{ my: 1 }} />
            <MenuItem disabled sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>
              Teams
            </MenuItem>
            {groups.map((team) => (
              <MenuItem key={`group-${team.id}`} value={`${SUBJECT_TYPES.GROUP}:${team.id}`} sx={{ pl: 3 }}>
                {team.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {(isLoadingAssignees || isLoadingMatrix || isLoadingPermissions) && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {selectedAssignee && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Selected:
            </Typography>
            <Chip
              label={selectedAssignee.name}
              color={selectedAssignee.type === SUBJECT_TYPES.GROUP ? 'secondary' : 'primary'}
              size={isMobile ? 'small' : 'medium'}
            />
            <Chip
              label={selectedAssignee.type === SUBJECT_TYPES.GROUP ? 'Team' : selectedAssignee.role || 'User'}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>
        )}
      </Paper>

      {/* Permission Matrix */}
      {selectedSubjectKey && (
        <>
          <Paper sx={{ overflow: 'hidden' }}>
            {projectTree.map((project) => {
              const tasks = project.tasks || [];

              return (
                <Accordion
                  key={project.id}
                  expanded={expandedProjects[project.id] || false}
                  onChange={() => toggleProjectExpand(project.id)}
                  disableGutters
                  sx={{
                    '&:before': { display: 'none' },
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'primary.50', '& .MuiAccordionSummary-content': { my: 1 } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <FolderIcon color="primary" sx={{ fontSize: 20 }} />
                      <Typography fontWeight={600} sx={{ flex: 1 }}>{project.name}</Typography>
                      <Chip label={`${tasks.length} tasks`} size={isMobile ? 'small' : 'medium'} variant="outlined" />
                      {renderEffectiveStatus(RESOURCE_TYPES.PROJECT, project.id)}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell width={MATRIX_COL_WIDTHS.expand} />
                            <TableCell sx={{ fontWeight: 600 }}>Resource</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: MATRIX_COL_WIDTHS.type }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Permission</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: MATRIX_COL_WIDTHS.effective }}>Effective</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* Project Row */}
                          <TableRow sx={{ bgcolor: 'primary.50' }}>
                            <TableCell />
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FolderIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography fontWeight={600}>{project.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label="Project" size={isMobile ? 'small' : 'medium'} color="primary" />
                            </TableCell>
                            <TableCell>
                              {renderPermissionToggle(RESOURCE_TYPES.PROJECT, project.id)}
                            </TableCell>
                            <TableCell>
                              {renderEffectiveStatus(RESOURCE_TYPES.PROJECT, project.id)}
                            </TableCell>
                          </TableRow>

                          {/* Tasks */}
                          {tasks.map((task) => (
                            <Fragment key={task.id}>
                              <TableRow hover>
                                <TableCell>
                                  {task.subtasks && task.subtasks.length > 0 && (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTaskExpand(task.id);
                                      }}
                                    >
                                      {expandedTasks[task.id] ? (
                                        <ExpandLessIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                                    <AssignmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography>{task.name}</Typography>
                                    {task.subtasks && task.subtasks.length > 0 && (
                                      <Chip label={`${task.subtasks.length} subtasks`} size={isMobile ? 'small' : 'medium'} variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip label="Task" size={isMobile ? 'small' : 'medium'} color="secondary" />
                                </TableCell>
                                <TableCell>
                                  {renderPermissionToggle(RESOURCE_TYPES.TASK, task.id)}
                                </TableCell>
                                <TableCell>
                                  {renderEffectiveStatus(RESOURCE_TYPES.TASK, task.id)}
                                </TableCell>
                              </TableRow>

                              {/* Subtasks */}
                              {task.subtasks && task.subtasks.length > 0 && expandedTasks[task.id] && task.subtasks.map((subtask) => (
                                <TableRow key={`${task.id}-${subtask.id}`} sx={{ bgcolor: 'grey.50' }}>
                                  <TableCell width={MATRIX_COL_WIDTHS.expand} />
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
                                      <SubdirectoryArrowRightIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                      <Typography variant="body2">{subtask.name}</Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell width={MATRIX_COL_WIDTHS.type}>
                                    <Chip label="Subtask" size={isMobile ? 'small' : 'medium'} variant="outlined" />
                                  </TableCell>
                                  <TableCell>
                                    {renderPermissionToggle(RESOURCE_TYPES.SUBTASK, subtask.id)}
                                  </TableCell>
                                  <TableCell width={MATRIX_COL_WIDTHS.effective}>
                                    {renderEffectiveStatus(RESOURCE_TYPES.SUBTASK, subtask.id)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSavePermissions}
              disabled={!selectedAssignee || isSaving || !canManagePermissions}
            >
              {isSaving ? 'Saving...' : canManagePermissions ? 'Save Permissions' : 'Read Only'}
            </Button>
            {isSaving && <CircularProgress size={20} />}
          </Box>
        </>
      )}
    </Box>
  );
};

export default PermissionSettings;
